import { useEffect, useRef, useState } from 'react'
import type { BuildProgressSnapshot, BuildSlotProgress } from '../api/client'

const SLOT_KEYS = ['case', 'movement', 'dial', 'hands', 'strap'] as const
const TOTAL_SLOTS = SLOT_KEYS.length

const SLOT_LABELS: Record<string, string> = {
  case: 'Case',
  movement: 'Movement',
  dial: 'Dial',
  hands: 'Hands',
  strap: 'Strap',
}

type BuildProgressMiniProps = {
  progress: BuildProgressSnapshot
  fallbackLabel?: string
  variant?: 'inline' | 'stage'
}

function isFilled(status: BuildSlotProgress['status']): boolean {
  return status === 'selected' || status === 'locked'
}

function chipClass(status: BuildSlotProgress['status'], isActive: boolean): string {
  if (isActive) return 'build-chip--active'
  switch (status) {
    case 'selected':
    case 'locked':
      return 'build-chip--done'
    case 'failed':
      return 'build-chip--failed'
    case 'searching':
      return 'build-chip--active'
    default:
      return 'build-chip--pending'
  }
}

function filledCount(progress: BuildProgressSnapshot): number {
  return SLOT_KEYS.filter((key) => isFilled(progress.slots[key]?.status ?? 'pending')).length
}

export function BuildProgressMini({ progress, fallbackLabel, variant = 'inline' }: BuildProgressMiniProps) {
  const isSizing = progress.phase === 'sizing'
  const filled = filledCount(progress)
  const fillPercent = isSizing ? 8 : Math.round((filled / TOTAL_SLOTS) * 100)
  const variantClass = variant === 'stage' ? ' build-progress-mini--stage' : ''

  const [captionVisible, setCaptionVisible] = useState(true)
  const prevLabel = useRef(progress.label)
  const label = progress.label || fallbackLabel || 'Building your watch…'

  useEffect(() => {
    if (label === prevLabel.current) return
    prevLabel.current = label
    setCaptionVisible(false)
    const timer = window.setTimeout(() => setCaptionVisible(true), 100)
    return () => window.clearTimeout(timer)
  }, [label])

  if (isSizing) {
    return (
      <div className={`build-progress-mini${variantClass}`}>
        <div className="build-progress-mini-prelude" aria-hidden="true">
          <span className="build-dot-pulse" />
          <span className="build-dot-pulse" />
          <span className="build-dot-pulse" />
        </div>
        <p className={`build-progress-mini-label ${captionVisible ? 'is-visible' : ''}`}>{label}</p>
      </div>
    )
  }

  return (
    <div className={`build-progress-mini${variantClass}`}>
      <div className="build-progress-mini-header">
        <div className="build-progress-mini-track" aria-hidden="true">
          <span className="build-progress-mini-fill" style={{ width: `${fillPercent}%` }} />
        </div>
        <p className="build-progress-mini-count" aria-label={`${filled} of ${TOTAL_SLOTS} parts selected`}>
          <span className="build-progress-mini-count-value">{filled}</span>
          <span className="build-progress-mini-count-sep">/</span>
          <span className="build-progress-mini-count-total">{TOTAL_SLOTS}</span>
          <span className="build-progress-mini-count-label">parts</span>
        </p>
      </div>

      <ul className="build-progress-mini-chips" aria-label="Watch parts">
        {SLOT_KEYS.map((slotKey) => {
          const slot = progress.slots[slotKey] ?? {
            status: 'pending' as const,
            part_id: null,
            part_name: null,
          }
          const isActive = progress.active_slot === slotKey && slot.status === 'searching'

          return (
            <li
              key={slotKey}
              className={`build-chip ${chipClass(slot.status, isActive)}`}
              aria-current={isActive ? 'step' : undefined}
            >
              <span className="build-chip-icon" aria-hidden="true">
                {isFilled(slot.status) ? '✓' : slot.status === 'failed' ? '!' : '·'}
              </span>
              <span className="build-chip-label">{SLOT_LABELS[slotKey]}</span>
              {slot.part_name ? (
                <span className="build-chip-part" title={slot.part_name}>
                  {slot.part_name}
                </span>
              ) : null}
            </li>
          )
        })}
        {(() => {
          const crown = progress.slots.crown
          if (!crown || crown.status === 'pending') return null
          const isActive = progress.active_slot === 'crown' && crown.status === 'searching'
          return (
            <li
              key="crown"
              className={`build-chip build-chip--optional ${chipClass(crown.status, isActive)}`}
              aria-current={isActive ? 'step' : undefined}
            >
              <span className="build-chip-icon" aria-hidden="true">
                {isFilled(crown.status) ? '✓' : crown.status === 'failed' ? '!' : '·'}
              </span>
              <span className="build-chip-label">Crown</span>
              {crown.part_name ? (
                <span className="build-chip-part" title={crown.part_name}>
                  {crown.part_name}
                </span>
              ) : null}
            </li>
          )
        })()}
      </ul>

      <p className={`build-progress-mini-label ${captionVisible ? 'is-visible' : ''}`}>{label}</p>
    </div>
  )
}
